import { Box, Center, SimpleGrid, Text } from '@chakra-ui/react';
import { Grid, gridConfig } from '@time-line/components/common';
import { clamp } from '@time-line/components/utils';
import {
  MixedContentContainer,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import { Fragment } from 'react';
import type { ABPaths as ABPathsProps } from 'types/cms/blocks';

const { gridPxClamp } = gridConfig;

export function ABPaths({
  title,
  subtitle,
  paths,
  showABOverlay,
}: ABPathsProps) {
  const image1Props = useImage(paths[0].image, { ratio: 3 / 2 });
  const image2Props = useImage(paths[1].image, { ratio: 3 / 2 });
  const circleSize = clamp('80px', '160px');

  return (
    <Box className="section--ab-paths" my="2xl">
      <Grid>
        <Grid.Inner gap="sm" mb="lg">
          <Box gridColumn={{ base: 'span 12', lg: 'span 6' }}>
            <Text as="h2" size="h2">
              {title}
            </Text>
          </Box>
          {subtitle && (
            <Box gridColumn={{ base: 'span 12', lg: '8 / 13' }}>
              <Text size="body2" color="gray.600">
                {subtitle}
              </Text>
            </Box>
          )}
        </Grid.Inner>
      </Grid>
      <Grid
        overflowX="auto"
        sx={{
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Grid.Inner
          width={{ base: '150vw', lg: '100%' }}
          gridColumnGap="sm"
          pr={{ base: gridPxClamp, lg: 0 }}
        >
          <Box
            display="grid"
            gridColumn="span 12"
            gridTemplateColumns="repeat(2, 1fr)"
            gridColumnGap={{ base: '3xs', lg: 'sm' }}
            alignItems="start"
          >
            {paths.map((path, index) => {
              const imageProps = index === 0 ? image1Props : image2Props;

              return (
                <Fragment key={index}>
                  <Box
                    className={`path-${index + 1}`}
                    gridColumn={index + 1}
                    gridRow={1}
                  >
                    <Box
                      borderRadius="base"
                      overflow="hidden"
                      mb="sm"
                      position="relative"
                    >
                      {showABOverlay && (
                        <Center
                          position="absolute"
                          top={0}
                          right={0}
                          bottom={0}
                          left={0}
                          zIndex={1}
                        >
                          <Center
                            borderRadius="full"
                            bgColor="blackAlpha.50"
                            width={circleSize}
                            height={circleSize}
                            backdropFilter="blur(26px)"
                          >
                            <Text size="h1" as="h3" color="white">
                              {index === 0 ? 'A' : 'B'}
                            </Text>
                          </Center>
                        </Center>
                      )}
                      {imageProps && (
                        <Image
                          {...imageProps}
                          sizes="(min-width: 1920px) 888px, 45vw"
                        />
                      )}
                    </Box>
                    {path.title && (
                      <Text
                        as="h4"
                        size="body2"
                        color={
                          path.isDeEmphasised && index === 0
                            ? 'blackAlpha.700'
                            : 'initial'
                        }
                        pb="sm"
                      >
                        {path.title}
                      </Text>
                    )}
                  </Box>
                  {path.facts.map((fact, factIndex) => (
                    <SimpleGrid
                      className={`fact-${factIndex + 1}`}
                      key={factIndex}
                      columns={{ base: 1, lg: 2 }}
                      borderTop="solid 1px"
                      borderColor="blackAlpha.250"
                      pt="sm"
                      pb={factIndex + 1 < path.facts.length ? 'lg' : 0}
                      gridColumnGap="sm"
                      style={{
                        gridRow: factIndex + 2,
                        gridColumn: index + 1,
                      }}
                    >
                      <Text
                        as="h5"
                        color={
                          path.isDeEmphasised && index === 0
                            ? 'blackAlpha.700'
                            : 'gray.600'
                        }
                        size={{ base: 'body2', lg: 'h4' }}
                        pb="4xs"
                      >
                        {fact.title}
                      </Text>
                      <Box color="blackAlpha.700">
                        <RichTextResolver
                          blocks={fact.description}
                          container={MixedContentContainer}
                        />
                      </Box>
                    </SimpleGrid>
                  ))}
                </Fragment>
              );
            })}
          </Box>
        </Grid.Inner>
      </Grid>
    </Box>
  );
}
